import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { api } from '@/utils/api';
import type {
  CampaignDeliverable,
  DeliverableDecision,
  DeliverableFeedback,
  DeliverableReview,
} from '@/types/portal';

export interface DeliverableReviewState {
  statusOf: (deliverableId: string) => string;
  threadOf: (deliverableId: string) => DeliverableFeedback[];
  reviewOf: (deliverableId: string) => DeliverableReview | undefined;
  /** Records a review decision (or a plain note) and updates the status. */
  decide: (
    deliverable: CampaignDeliverable,
    decision: DeliverableDecision,
    text: string,
    timecode?: string,
  ) => Promise<void>;
  /** Approves several deliverables at once with a shared note. */
  approveMany: (deliverables: CampaignDeliverable[], note?: string) => Promise<void>;
  /** True while a decision is being saved. */
  saving: boolean;
  /** The last save error, cleared by the next successful save. */
  error: string;
  clearError: () => void;
}

/**
 * Review state for a campaign's deliverables. The list itself (with each item's
 * review and feedback thread) comes from `GET /campaigns/:id/contents` and is
 * owned by the page; decisions are saved through the API and the saved result
 * replaces the item, so the list, counters and performance tab stay in step.
 *
 * Not yet stored by the backend (docs/BACKEND_GAPS.md): approval notes, timecodes
 * and plain notes without a decision.
 */
export function useDeliverableReviews(
  campaignId: string | undefined,
  deliverables: CampaignDeliverable[],
  setDeliverables: Dispatch<SetStateAction<CampaignDeliverable[]>>,
): DeliverableReviewState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const byId = useMemo(
    () => Object.fromEntries(deliverables.map((item) => [item.id, item])),
    [deliverables],
  );

  const statusOf = useCallback(
    (deliverableId: string) => byId[deliverableId]?.status ?? 'Draft',
    [byId],
  );

  const threadOf = useCallback(
    (deliverableId: string) => byId[deliverableId]?.review?.feedback ?? [],
    [byId],
  );

  const reviewOf = useCallback(
    (deliverableId: string) => byId[deliverableId]?.review ?? undefined,
    [byId],
  );

  const save = useCallback(async (work: () => Promise<void>) => {
    setSaving(true);
    setError('');
    try {
      await work();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the review.');
    } finally {
      setSaving(false);
    }
  }, []);

  const decide = useCallback(
    (deliverable: CampaignDeliverable, decision: DeliverableDecision, text: string) =>
      save(async () => {
        // Production's routes: changes go to /revision, approvals (and notes) to /review.
        const updated = await api<CampaignDeliverable>(
          `/campaigns/${campaignId}/contents/${deliverable.id}/${decision === 'changes_requested' ? 'revision' : 'review'}`,
          { method: 'POST', body: JSON.stringify({ decision, text }) },
        );
        setDeliverables((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      }),
    [campaignId, save, setDeliverables],
  );

  const approveMany = useCallback(
    (items: CampaignDeliverable[]) =>
      save(async () => {
        if (items.length === 0) return;
        const result = await api<{ approved: number; deliverables: CampaignDeliverable[] }>(
          `/campaigns/${campaignId}/contents/bulk-approve`,
          { method: 'POST', body: JSON.stringify({ ids: items.map((item) => item.id) }) },
        );
        setDeliverables(result.deliverables);
      }),
    [campaignId, save, setDeliverables],
  );

  const clearError = useCallback(() => setError(''), []);

  return useMemo(
    () => ({ statusOf, threadOf, reviewOf, decide, approveMany, saving, error, clearError }),
    [statusOf, threadOf, reviewOf, decide, approveMany, saving, error, clearError],
  );
}
